import React, { useState, useEffect, Suspense } from 'react';
import { Link } from 'react-router-dom';
import Apiservice from '../services/Apiservice';
import CommonFunctions from '../components/CommonFunctions';
import { RenderNavigation, RenderFooter, RenderLeftAside, RenderRightAside } from '../components/ReusableFragments';
import { Helmet } from 'react-helmet';

// Lazy-loaded components
const Slider = React.lazy(() => import('react-slick'));
const Navigationbar = React.lazy(() => import('../components/Navigationbar'));
const Footer = React.lazy(() => import('../components/Footer'));

const FixturesAndResults = () => {
  const [tournamentId] = useState('3');
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [resultedMatches, setResultedMatches] = useState([]);
  const [previousData, setPreviousData] = useState({});
  const [todaySfs, setTodaySfs] = useState({});
  const [topBatters, setTopBatters] = useState([]);
  const [topBowlers, setTopBowlers] = useState([]);
  const [pointsTable, setPointsTable] = useState([]);
  const [posts, setPosts] = useState([]);
  const [birthdays, setBirthdays] = useState([]);

  useEffect(() => {
    Apiservice.getallfixturesresults(tournamentId)
      .then((response) => {
        setUpcomingMatches(response.data.fixtures || []);
        setPreviousData(response.data.previous_data || {});
        setResultedMatches(response.data.resulted_matches || []);
        setTodaySfs(response.data.total_sfs?.[0] || {});
        setPointsTable(response.data.points_table || []);
        setPosts(response.data.posts || []);
        setTopBatters(response.data.topbatters || []);
        setTopBowlers(response.data.topbowlers || []);
        setBirthdays(response.data.bdays || []);
      })
      .catch((error) => {
        console.error("Error fetching fixtures and results:", error);
      });
  }, [tournamentId]);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 2000,
    responsive: [
      { breakpoint: 768, settings: { slidesToShow: 2, slidesToScroll: 1, infinite: true, dots: true } },
      { breakpoint: 480, settings: { slidesToShow: 1, slidesToScroll: 1, initialSlide: 1 } }
    ]
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>WPL 2025 Fixtures & Results | WPL Cricinfo</title>
        <meta name="description" content="Check the latest WPL 2025 fixtures, upcoming matches, and results." />
        <meta name="keywords" content="WPL 2025, fixtures, results, cricket schedule" />
      </Helmet>
      <div className="bg-fluid"></div>
      <RenderNavigation />
      <div className="container-fluid">
        <nav aria-label="breadcrumb" className="">
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <Link to="/" className="text-muted text-decoration-none">Home</Link>
            </li>
            <li className="breadcrumb-item text-white active" aria-current="page">Fixtures & Results</li>
          </ol>
        </nav>
        <div className="row">
          <div className="col-md-12">
            <article className="">
              <div className="mx-4 mb-3">
                <Suspense fallback={<div>Loading Slider...</div>}>
                  <Slider {...settings}>
                    {["dc-w", "gg-w", "mi-w", "rcb-w", "upw-w"].map((team, index) => (
                      <div className="p-1" key={index}>
                        <img
                          src={`images/banner/${team}_wpl_banner_wplcricinfo.webp`}
                          alt={`${team}_wpl_2024_banner_wplcricinfo`}
                          className="img-fluid"
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </Slider>
                </Suspense>
              </div>
            </article>
          </div>
        </div>

        <main>
          <div className="row">
            <div className="col-md-9">
              <div className="card">
                <div className="card-body">
                  <h1 className="fs-5 mb-2">Women's Premier League 2025 Fixtures, Upcoming and Resulted Matches</h1>
                  <ul className="nav nav-pills mb-2" id="pills-tab" role="tablist">
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link"
                        id="pills-home-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-home"
                        type="button"
                        role="tab"
                        aria-controls="pills-home"
                        aria-selected="false"
                      >
                        Fixtures
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link active"
                        id="pills-profile-tab"
                        data-bs-toggle="pill"
                        data-bs-target="#pills-profile"
                        type="button"
                        role="tab"
                        aria-controls="pills-profile"
                        aria-selected="true"
                      >
                        Results
                      </button>
                    </li>
                  </ul>
                  <div className="tab-content" id="pills-tabContent">
                    <div
                      className="tab-pane fade"
                      id="pills-home"
                      role="tabpanel"
                      aria-labelledby="pills-home-tab"
                      tabIndex="0"
                    >
                      {upcomingMatches.length === 0 ? (
                        <div>No more matches, wait for the next season to start!</div>
                      ) : (
                        upcomingMatches.map((item, index) => (
                          <div className="card mb-2" key={index}>
                            <div className="card-header">
                              {item.formatted_match_no} WPL 2025 Match (N)
                            </div>
                            <div className="card-body">
                              <small className="text-muted">
                                {item.team1_shortcode || 'TBA'} vs {item.team2_shortcode || 'TBA'}
                              </small>
                              <h2 className="card-title fs-5 mb-0">
                                {item.team1_name || 'TBA'} vs {item.team2_name || 'TBA'}
                              </h2>
                              <small className="card-text">
                                <i className="bi bi-calendar-event text-primary"></i>{' '}
                                {CommonFunctions.getDateFormat(item.match_date)}
                                <i className="bi bi-clock text-primary ps-4"></i>{' '}
                                {CommonFunctions.getFormattedTime(item.match_time)}
                              </small>
                              <div className="match-venue">
                                <i className="bi bi-geo-alt"></i> {item.venue_name}, {item.city}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div
                      className="tab-pane fade show active"
                      id="pills-profile"
                      role="tabpanel"
                      aria-labelledby="pills-profile-tab"
                      tabIndex="0"
                    >
                      {resultedMatches.length === 0 ? (
                        <div>No results available yet.</div>
                      ) : (
                        resultedMatches.map((match) => (
                          <div className="card mb-2 shadow" key={match.match_id}>
                            <div className="card-header">
                              {match.formatted_match_no === '21st'
                                ? 'Eliminator'
                                : match.formatted_match_no === '22nd'
                                ? 'Finals'
                                : match.formatted_match_no}{' '}
                              WPL 2025 Match (N)
                            </div>
                            <div className="card-body">
                              <small className="text-muted">
                                <img
                                  src={`images/logos/${match.team1_shortcode.toLowerCase()}-sm_wplcricinfo.jpg`}
                                  style={{ width: '35px' }}
                                  alt={match.team1_shortcode}
                                  loading="lazy"
                                />
                                <span className="mx-4">vs</span>
                                <img
                                  src={`images/logos/${match.team2_shortcode.toLowerCase()}-sm_wplcricinfo.jpg`}
                                  style={{ width: '35px' }}
                                  alt={match.team2_shortcode}
                                  loading="lazy"
                                />
                              </small>
                              <br />
                              {previousData[`match_id_${match.match_id}`]?.map((team) => (
                                <span key={team.team_id}>
                                  <span className="pe-2 small">
                                    {team.team_shortcode}: {parseInt(team.total_scored_runs) + parseInt(team.total_extras)}/
                                    {team.total_out_count}{' '}
                                  </span>
                                </span>
                              ))}
                              <h2 className="card-title fs-5 my-2">
                                <Link
                                  to={`/match/${match.match_id}/${match.team1_name
                                    .toLowerCase()
                                    .replace(/\s+/g, '-')}-vs-${match.team2_name
                                    .toLowerCase()
                                    .replace(/\s+/g, '-')}`}
                                >
                                  {match.team1_name} vs {match.team2_name}
                                </Link>
                              </h2>
                              <div className="float-end">
                                {match.winner_id === match.team1_id && (
                                  <>
                                    Winner: <i className="bi bi-trophy text-info"></i>{' '}
                                    <img
                                      src={`images/logos/${match.team1_shortcode.toLowerCase()}-sm_wplcricinfo.jpg`}
                                      style={{ width: '35px' }}
                                      alt={match.team1_shortcode}
                                      loading="lazy"
                                    />
                                  </>
                                )}
                                {match.winner_id === match.team2_id && (
                                  <>
                                    Winner: <i className="bi bi-trophy text-info"></i>{' '}
                                    <img
                                      src={`images/logos/${match.team2_shortcode.toLowerCase()}-sm_wplcricinfo.jpg`}
                                      style={{ width: '35px' }}
                                      alt={match.team2_shortcode}
                                      loading="lazy"
                                    />
                                  </>
                                )}
                              </div>
                              <small className="card-text">
                                <i className="bi bi-calendar-event text-primary pe-1"></i>{' '}
                                {CommonFunctions.getDateFormat(match.match_date)}
                              </small>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-3">
              <RenderRightAside todaySfs={todaySfs} pointsTable={pointsTable} playerBdays={birthdays} posts={posts} />
            </div>
          </div>
        </main>
      </div>
      <RenderFooter />
    </React.Fragment>
  );
};

export default FixturesAndResults;